import {
  MessageSquareText,
  Image as ImageIcon,
  Volume2,
  Mic,
  Boxes,
  ListOrdered,
  AudioLines,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import type { Modality } from '@/types'

/**
 * Modality string accepted by the icon. Extends the catalog `Modality` union
 * with a forward-compatible `realtime` case (reserved for #957) and accepts
 * any string so the component never crashes on an unrecognized modality.
 */
export type IconModality = Modality | 'realtime' | string

interface ModalitySpec {
  icon: LucideIcon
  /** Semantic foreground color class. */
  colorClass: string
  label: string
}

const MODALITY_SPECS: Record<string, ModalitySpec> = {
  text: { icon: MessageSquareText, colorClass: 'text-primary', label: 'Text' },
  image: { icon: ImageIcon, colorClass: 'text-info', label: 'Image' },
  tts: { icon: Volume2, colorClass: 'text-success', label: 'TTS' },
  stt: { icon: Mic, colorClass: 'text-warning', label: 'STT' },
  embedding: { icon: Boxes, colorClass: 'text-secondary-foreground', label: 'Embedding' },
  rerank: { icon: ListOrdered, colorClass: 'text-muted-foreground', label: 'Rerank' },
  realtime: { icon: AudioLines, colorClass: 'text-destructive', label: 'Realtime' },
}

const FALLBACK: ModalitySpec = { icon: HelpCircle, colorClass: 'text-muted-foreground', label: 'Unknown' }

export interface ModalityIconProps {
  modality: IconModality
  /** Pixel size of the icon (lucide `size`). */
  size?: number
  className?: string
  /** Render the modality label text next to the icon. */
  withLabel?: boolean
}

export function getModalitySpec(modality: IconModality): { colorClass: string; label: string } {
  const spec = MODALITY_SPECS[modality] ?? FALLBACK
  return { colorClass: spec.colorClass, label: spec.label }
}

export function ModalityIcon({ modality, size = 16, className, withLabel = false }: ModalityIconProps) {
  const spec = MODALITY_SPECS[modality] ?? FALLBACK
  const Icon = spec.icon

  return (
    <span className={cn('inline-flex items-center gap-1', spec.colorClass, className)}>
      <Icon size={size} aria-hidden />
      {withLabel && <span className="text-xs font-medium">{spec.label}</span>}
    </span>
  )
}
